import { useEffect, useMemo, useRef, useState } from 'react';
import { logDataModels } from '../dataModelsLogger';
import { loadTableColumns } from './loadTableColumns';
import type { GovernancePolicy, PolicyAsset, PolicyColumn } from './types';

const LOAD_CHUNK = 8;

export type PolicyColumnChoice = {
  name: string;
  label: string;
  qualified: string;
  assetId: string;
  group: string;
};

export type PolicyColumnGroup = {
  id: string;
  label: string;
  columns: PolicyColumnChoice[];
};

export function assetFieldLabel(asset: Pick<PolicyAsset, 'name' | 'path'>): string {
  return (asset.path || '').trim() || asset.name;
}

export function assetsForRowFilter(policy: Pick<GovernancePolicy, 'target'>, assets: PolicyAsset[]): PolicyAsset[] {
  if (policy.target.mode === 'selected') {
    return assets.filter((asset) => policy.target.assetIds.includes(asset.id));
  }
  if (policy.target.mode === 'current') {
    const current = assets.filter((asset) => policy.target.assetIds.includes(asset.id));
    return current.length ? current : assets.slice(0, 1);
  }
  return assets;
}

export function columnGroups(assets: PolicyAsset[]): PolicyColumnGroup[] {
  return assets
    .map((asset) => {
      const group = assetFieldLabel(asset);
      const columns = asset.columns
        .filter((column) => column.name)
        .map((column) => ({
          name: column.name,
          label: column.type ? `${column.name} · ${column.type}` : column.name,
          qualified: `${group}.${column.name}`,
          assetId: asset.id,
          group,
        }))
        .sort((left, right) => left.name.localeCompare(right.name));
      return { id: asset.id, label: group, columns };
    })
    .filter((group) => group.columns.length)
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function columnChoices(assets: PolicyAsset[]): PolicyColumnChoice[] {
  return columnGroups(assets).flatMap((group) => group.columns);
}

export function columnQualifiedName(asset: Pick<PolicyAsset, 'name' | 'path'>, columnName: string): string {
  return `${assetFieldLabel(asset)}.${columnName}`;
}

export function columnMatchesRef(asset: PolicyAsset, column: PolicyColumn, ref: string): boolean {
  const normalized = ref.trim();
  if (!normalized || !column.name) return false;
  if (normalized === column.name) return true;
  if (normalized === `${asset.name}.${column.name}`) return true;
  return normalized === columnQualifiedName(asset, column.name);
}

export function resolveColumnRef(ref: string, assets: PolicyAsset[]): Array<{ asset: PolicyAsset; column: PolicyColumn; name: string; qualified: string }> {
  return assets.flatMap((asset) => asset.columns
    .filter((column) => columnMatchesRef(asset, column, ref))
    .map((column) => ({
      asset,
      column,
      name: column.name,
      qualified: columnQualifiedName(asset, column.name),
    })));
}

function withLoadedColumns(asset: PolicyAsset, loaded: Record<string, PolicyColumn[]>): PolicyAsset {
  if (!(asset.id in loaded)) return asset;
  return { ...asset, columns: loaded[asset.id] };
}

function assetTryKey(asset: PolicyAsset): string {
  return `${asset.id}|${asset.sourceId || ''}|${asset.path || ''}`;
}

export function usePolicyAssetColumns(assets: PolicyAsset[], options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const [loaded, setLoaded] = useState<Record<string, PolicyColumn[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const triedRef = useRef(new Set<string>());
  const assetsRef = useRef(assets);
  assetsRef.current = assets;
  const signature = useMemo(
    () => assets.map((asset) => assetTryKey(asset)).join(';'),
    [assets],
  );

  useEffect(() => {
    if (!enabled) return;
    const current = assetsRef.current;
    if (current.some((asset) => !asset.sourceId && !asset.columns.length)) {
      logDataModels('warning', 'policy_asset_columns_missing_source', {
        names: current.filter((asset) => !asset.sourceId).map((asset) => asset.name).slice(0, 8),
      });
    }
    const missing = current.filter((asset) => Boolean(asset.sourceId) && !triedRef.current.has(assetTryKey(asset)));
    if (!missing.length) {
      setLoading(false);
      return;
    }
    missing.forEach((asset) => triedRef.current.add(assetTryKey(asset)));
    let cancelled = false;
    const blocking = missing.some((asset) => !asset.columns.length);
    setLoading(blocking);
    setError('');
    logDataModels('app', 'policy_asset_columns_load', {
      count: missing.length,
      names: missing.slice(0, 8).map((asset) => asset.name),
    });

    const run = async () => {
      const next: Record<string, PolicyColumn[]> = {};
      const failures: string[] = [];
      for (let index = 0; index < missing.length; index += LOAD_CHUNK) {
        if (cancelled) return;
        const chunk = missing.slice(index, index + LOAD_CHUNK);
        const rows = await Promise.allSettled(chunk.map(async (asset) => {
          const columns = await loadTableColumns(asset.sourceId || '', asset.name, asset.path);
          return { id: asset.id, name: asset.name, columns };
        }));
        rows.forEach((row, rowIndex) => {
          const asset = chunk[rowIndex];
          if (row.status === 'fulfilled') {
            if (row.value.columns.length) next[row.value.id] = row.value.columns;
            return;
          }
          const message = row.reason instanceof Error ? row.reason.message : 'Unable to load columns';
          failures.push(`${asset.name}: ${message}`);
          logDataModels('warning', 'policy_asset_columns_asset_failed', { assetId: asset.id, name: asset.name, message });
        });
      }
      if (cancelled) return;
      if (Object.keys(next).length) {
        setLoaded((currentLoaded) => ({ ...currentLoaded, ...next }));
      }
      const columnCount = Object.values(next).reduce((sum, columns) => sum + columns.length, 0);
      if (failures.length === missing.length) {
        const message = failures[0] || 'Unable to load columns';
        setError(message);
        logDataModels('error', 'policy_asset_columns_failed', { count: failures.length, message });
      } else if (failures.length) {
        setError(`Columns loaded for some tables. ${failures.length} table${failures.length === 1 ? '' : 's'} could not be read.`);
        logDataModels('warning', 'policy_asset_columns_partial', { failed: failures.length, ok: missing.length - failures.length, columns: columnCount });
      } else if (!columnCount) {
        logDataModels('warning', 'policy_asset_columns_empty', { count: missing.length });
      } else {
        logDataModels('app', 'policy_asset_columns_load_ok', { assets: missing.length, columns: columnCount });
      }
      setLoading(false);
    };

    void run().catch((error: unknown) => {
      if (cancelled) return;
      const message = error instanceof Error ? error.message : 'Unable to load columns';
      setError(message);
      setLoading(false);
      logDataModels('error', 'policy_asset_columns_failed', { message });
    });

    return () => {
      cancelled = true;
      missing.forEach((asset) => triedRef.current.delete(assetTryKey(asset)));
    };
  }, [enabled, signature]);

  const enriched = useMemo(
    () => assets.map((asset) => withLoadedColumns(asset, loaded)),
    [assets, loaded],
  );

  return { assets: enriched, loading, error };
}
